import { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useGameStore } from '../store/gameStore';
import { playCoin, playComplete, playNotify } from '../utils/sounds';

const DAY_REWARDS = [5, 8, 12, 15, 20, 25, 50];
const DAY_LABELS = ['一', '二', '三', '四', '五', '六', '七'];

export default function DailyCheckin({ onClose }: { onClose: () => void }) {
  const { checkinDates, checkinStreak, lastCheckinDate, dailyCheckin } = useGameStore();
  const [checkedIn, setCheckedIn] = useState(false);
  const [reward, setReward] = useState(0);
  const [showConfetti, setShowConfetti] = useState(false);

  const today = new Date().toISOString().split('T')[0];
  const alreadyCheckedIn = lastCheckinDate === today;

  // Current day in 7-day cycle (1-7)
  const currentDayInCycle = useMemo(() => {
    if (alreadyCheckedIn) {
      return ((checkinStreak - 1) % 7) + 1;
    }
    // If continuing streak
    const yesterday = new Date(Date.now() - 86400000).toISOString().split('T')[0];
    if (lastCheckinDate === yesterday) {
      return (checkinStreak % 7) + 1;
    }
    return 1; // Reset
  }, [alreadyCheckedIn, checkinStreak, lastCheckinDate]);

  // Past 7 days check-in status
  const weekStatus = useMemo(() => {
    const status: { date: string; label: string; checkedIn: boolean; isToday: boolean }[] = [];
    for (let i = 6; i >= 0; i--) {
      const d = new Date(Date.now() - i * 86400000);
      const dateStr = d.toISOString().split('T')[0];
      status.push({
        date: dateStr,
        label: d.toLocaleDateString('zh-CN', { weekday: 'short' }).replace('周', ''),
        checkedIn: checkinDates.includes(dateStr),
        isToday: dateStr === today,
      });
    }
    return status;
  }, [checkinDates, today]);

  const handleCheckin = () => {
    if (alreadyCheckedIn || checkedIn) return;
    const earned = dailyCheckin();
    setReward(earned);
    setCheckedIn(true);
    setShowConfetti(true);
    playNotify();
    setTimeout(() => playCoin(), 200);
    setTimeout(() => playComplete(), 500);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[9998] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.8, y: 30, opacity: 0 }}
        animate={{ scale: 1, y: 0, opacity: 1 }}
        exit={{ scale: 0.9, y: 20, opacity: 0 }}
        transition={{ type: 'spring', stiffness: 300, damping: 25 }}
        className="bg-white dark:bg-gray-800 rounded-3xl p-6 w-full max-w-sm shadow-2xl relative overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Background decoration */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-gradient-to-br from-amber-100/50 to-orange-100/50 dark:from-amber-900/20 dark:to-orange-900/20" />
          <div className="absolute -bottom-8 -left-8 w-32 h-32 rounded-full bg-gradient-to-br from-emerald-100/40 to-teal-100/40 dark:from-emerald-900/20 dark:to-teal-900/20" />
        </div>

        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 w-7 h-7 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-sm transition-colors z-10"
        >
          ✕
        </button>

        {/* Title */}
        <div className="text-center mb-5 relative">
          <motion.div
            animate={{ rotate: [0, -5, 5, 0] }}
            transition={{ duration: 1, repeat: Infinity, repeatDelay: 3 }}
            className="text-4xl mb-2"
          >
            📅
          </motion.div>
          <h2 className="text-lg font-bold text-gray-800 dark:text-gray-100">每日签到</h2>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            连续签到 <span className="text-amber-600 dark:text-amber-400 font-semibold">{alreadyCheckedIn || checkedIn ? checkinStreak : (lastCheckinDate === new Date(Date.now() - 86400000).toISOString().split('T')[0] ? checkinStreak : 0)}</span> 天
          </p>
        </div>

        {/* 7-day reward schedule */}
        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-2xl p-4 mb-4 relative">
          <p className="text-[10px] text-gray-500 dark:text-gray-400 text-center mb-3 font-medium">7日连签奖励</p>
          <div className="grid grid-cols-7 gap-1.5">
            {DAY_REWARDS.map((r, i) => {
              const dayNum = i + 1;
              const isPast = dayNum < currentDayInCycle;
              const isCurrent = dayNum === currentDayInCycle;
              const isCompleted = (alreadyCheckedIn || checkedIn) && dayNum <= currentDayInCycle;
              
              return (
                <div key={i} className="flex flex-col items-center">
                  <motion.div
                    className={`w-9 h-9 rounded-xl flex items-center justify-center text-xs font-bold mb-1 border-2 transition-all ${
                      isCompleted
                        ? 'bg-gradient-to-br from-emerald-400 to-teal-500 border-emerald-300 text-white shadow-md shadow-emerald-200 dark:shadow-none'
                        : isCurrent && !(alreadyCheckedIn || checkedIn)
                          ? 'bg-gradient-to-br from-amber-400 to-orange-500 border-amber-300 text-white shadow-md shadow-amber-200 dark:shadow-none animate-pulse'
                          : 'bg-white dark:bg-gray-600 border-gray-200 dark:border-gray-500 text-gray-400 dark:text-gray-400'
                    }`}
                    whileHover={{ scale: 1.05 }}
                  >
                    {isCompleted ? '✓' : dayNum === 7 ? '🎁' : `+${r}`}
                  </motion.div>
                  <span className={`text-[9px] ${
                    isCurrent ? 'text-amber-600 dark:text-amber-400 font-bold' : 'text-gray-400 dark:text-gray-500'
                  }`}>
                    第{DAY_LABELS[i]}天
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Past 7 days calendar */}
        <div className="mb-5">
          <p className="text-[10px] text-gray-500 dark:text-gray-400 text-center mb-2">近7日签到记录</p>
          <div className="flex justify-center gap-2">
            {weekStatus.map((day, i) => (
              <div key={i} className="flex flex-col items-center gap-1">
                <span className={`text-[9px] ${day.isToday ? 'text-emerald-600 dark:text-emerald-400 font-bold' : 'text-gray-400 dark:text-gray-500'}`}>
                  {day.isToday ? '今' : day.label}
                </span>
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] ${
                  day.checkedIn || (day.isToday && checkedIn)
                    ? 'bg-emerald-500 text-white'
                    : day.isToday
                      ? 'bg-amber-100 dark:bg-amber-900/30 border-2 border-amber-400 text-amber-600'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-300 dark:text-gray-600'
                }`}>
                  {day.checkedIn || (day.isToday && checkedIn) ? '✓' : day.isToday ? '·' : '·'}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Check-in button or result */}
        {(alreadyCheckedIn || checkedIn) ? (
          <motion.div
            initial={checkedIn ? { scale: 0.8, opacity: 0 } : false}
            animate={{ scale: 1, opacity: 1 }}
            className="text-center"
          >
            <div className="bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 rounded-xl p-4 border border-emerald-200 dark:border-emerald-800">
              <p className="text-emerald-700 dark:text-emerald-300 font-semibold text-sm">
                ✅ 今日已签到
              </p>
              {reward > 0 && (
                <motion.p
                  initial={{ y: 10, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.3 }}
                  className="text-amber-600 dark:text-amber-400 text-xs mt-1 font-medium"
                >
                  获得 +{reward} 能量 ⚡
                </motion.p>
              )}
              <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-2">明天继续签到获得更多奖励哦~</p>
            </div>
          </motion.div>
        ) : (
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={handleCheckin}
            className="w-full py-3.5 rounded-2xl bg-gradient-to-r from-amber-400 via-orange-500 to-red-500 text-white font-bold text-sm shadow-lg shadow-orange-200 dark:shadow-none hover:shadow-xl transition-all active:shadow-md"
          >
            <span className="flex items-center justify-center gap-2">
              <span>签到领取</span>
              <span className="bg-white/20 px-2 py-0.5 rounded-lg text-xs">+{DAY_REWARDS[currentDayInCycle - 1]} ⚡</span>
            </span>
          </motion.button>
        )}

        {/* Confetti effect */}
        <AnimatePresence>
          {showConfetti && (
            <>
              {Array.from({ length: 20 }).map((_, i) => (
                <motion.div
                  key={i}
                  className="absolute w-2 h-2 rounded-full pointer-events-none"
                  style={{
                    background: ['#FFD700', '#FF6B6B', '#4ECDC4', '#45B7D1', '#96E6A1', '#DDA0DD', '#FFA07A', '#98D8C8'][i % 8],
                    left: '50%',
                    top: '40%',
                  }}
                  initial={{ x: 0, y: 0, opacity: 1, scale: 1 }}
                  animate={{
                    x: (Math.random() - 0.5) * 200,
                    y: (Math.random() - 0.5) * 200,
                    opacity: 0,
                    scale: [1, 1.5, 0],
                    rotate: Math.random() * 360,
                  }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 1.2, ease: 'easeOut' }}
                />
              ))}
            </>
          )}
        </AnimatePresence>
      </motion.div>
    </motion.div>
  );
}
